/**
 * «Bu sertifikayla gönderilecek e-posta» — the one data path the editing surface uses.
 *
 * ONE SOURCE, TWO ENTRANCES. The preparation wizard's step and the editor's tab open the same
 * content through the same port and render the same component. There is no second store, no second
 * default text and no browser-only copy of the message: everything a screen shows here came from
 * the server, and everything it saves goes back to the server.
 *
 * The port is small on purpose — read, save, reset, preview. None of them sends an e-mail, mints a
 * claim token or publishes anything; issuing is a separate act on a separate screen.
 */
import { createContext, useContext } from 'react';
import type { ApiClient } from '../../api/client';

/** What the screen edits, plus what it needs to offer the standard message without a round trip. */
export interface CertificateEmailView {
  /** Null while the preparation wizard has not created the certificate yet. */
  templateId: string | null;
  subject: string;
  bodyText: string;
  bodyHtml: string | null;
  format: 'text' | 'html';
  /** False while this certificate still uses the standard message. */
  custom: boolean;
  standard: { subject: string; bodyText: string; bodyHtml: string | null };
  /** Which act the standard message describes: an invitation to accept, or a notice of publication. */
  kind: 'claim' | 'notice';
  /** Exactly the names the send path substitutes — the screen lists these and invents none. */
  placeholders: string[];
  updatedAt: string | null;
}

/**
 * What the preparation wizard knows about a certificate that does not exist yet.
 *
 * The issuer's NAME is deliberately absent: the server reads it from the profile, because the
 * message it composes is signed by the issuer and a browser-supplied name would put anything it
 * liked into it.
 */
export interface CertificateEmailContext {
  title: string;
  credentialType?: string | null;
  publicationPolicy?: string | null;
  issuerProfileId?: string | null;
}

export interface CertificateEmailSaved extends CertificateEmailView {
  /** What the server's cleaning did to the author's markup, so the screen can say so. */
  sanitized: { changed: boolean; notes: string[] };
}

export interface CertificateEmailPreview {
  subject: string;
  text: string;
  html: string;
  changed: boolean;
  notes: string[];
  /** The visibly synthetic values the preview used. Never a recipient, never a real token. */
  sample: Record<string, string>;
  /** The cleaned source the preview was rendered from — what «save this» would really store. */
  cleanedHtml: string | null;
  cleanedText: string;
}

export interface CertificateEmailDraft {
  subject: string;
  bodyText: string;
  bodyHtml: string | null;
  format: 'text' | 'html';
}

export interface TemplateEmailPort {
  read(templateId: string): Promise<CertificateEmailView>;
  save(templateId: string, draft: CertificateEmailDraft): Promise<CertificateEmailSaved>;
  /** «Standart e-postaya dön» — the certificate's own content is removed, nothing else is touched. */
  reset(templateId: string): Promise<CertificateEmailView>;
  /** What one recipient would see, cleaned by the same rules the send path uses. */
  preview(templateId: string, draft: CertificateEmailDraft): Promise<CertificateEmailPreview>;
  /**
   * The same standard message and the same preview, for a certificate that is still being prepared.
   *
   * It creates nothing — no template, no record, no claim link — so the wizard's step can offer the
   * product's own message, its HTML to copy and a real preview at the moment the author asks for
   * them, instead of sending them to another screen after the certificate exists.
   */
  draft(
    context: CertificateEmailContext,
    content: CertificateEmailDraft | null,
  ): Promise<{ view: CertificateEmailView; preview: CertificateEmailPreview }>;
}

/** The product's own path: four routes under the template the author is already allowed to edit. */
export function apiTemplateEmail(api: ApiClient): TemplateEmailPort {
  const path = (templateId: string) => `/api/v1/internal/templates/${encodeURIComponent(templateId)}/email`;
  return {
    read: (templateId) => api.json<CertificateEmailView>(path(templateId)),
    save: (templateId, draft) =>
      api.json<CertificateEmailSaved>(path(templateId), { method: 'PATCH', body: draft }),
    reset: (templateId) => api.json<CertificateEmailView>(path(templateId), { method: 'DELETE' }),
    preview: (templateId, draft) =>
      api.json<CertificateEmailPreview>(`${path(templateId)}/preview`, {
        method: 'POST',
        body: draft,
      }),
    draft: (context, content) =>
      api.json<{ view: CertificateEmailView; preview: CertificateEmailPreview }>(
        '/api/v1/internal/templates/email/draft',
        { method: 'POST', body: { ...context, content } },
      ),
  };
}

const TemplateEmailContext = createContext<TemplateEmailPort | null>(null);

export const TemplateEmailProvider = TemplateEmailContext.Provider;

/**
 * The port, or null where none is mounted.
 *
 * Null rather than a throw, and null rather than a silent local store: the frozen prototype surface
 * has no server behind it, and a screen that quietly edited a browser-only copy of a real
 * certificate's e-mail would be lying about what it saved. The panel renders its own «bu yüzeyde
 * kullanılamıyor» state instead.
 */
export function useTemplateEmail(): TemplateEmailPort | null {
  return useContext(TemplateEmailContext);
}
